"use client";

import Button from "@/components/ui/button";
import { FormControl, FormLabel, FormMessage } from "@/components/ui/form";
import FormField from "@/components/ui/form-field";
import Input from "@/components/ui/input";
import { cn } from "@/lib/utils";
import Image from "next/image";
import Link from "next/link";
import { useLoginForm } from "../hooks/useLoginForm";
import { useSocialLogin } from "../hooks/useSocialLogin";

const inputClass = cn("border border-[var(--gray-100)]");

const LoginForm = () => {
  const { form, onSubmit, isSubmitPending } = useLoginForm();
  const { signInWithGoogle, isSocialLoading } = useSocialLogin({
    setFormError: (message) => form.setError("root", { message }),
    clearFormError: () => form.clearErrors("root"),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = form;

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className={cn("space-y-4 sm:space-y-5 md:space-y-6")}
    >
      <FormField>
        <FormLabel>Email Address</FormLabel>
        <FormControl>
          <Input
            type="email"
            autoComplete="email"
            placeholder="i.e. davisjohn@example.com"
            className={inputClass}
            {...register("email")}
          />
        </FormControl>
        {errors.email && (
          <FormMessage
            className={cn("text-[var(--red-500)]")}
            error={errors.email.message}
          />
        )}
      </FormField>

      <FormField>
        <FormLabel>Password</FormLabel>
        <FormControl>
          <Input
            type="password"
            autoComplete="current-password"
            placeholder="Enter your password"
            showPasswordToggle
            className={inputClass}
            {...register("password")}
          />
        </FormControl>
        {errors.password && (
          <FormMessage
            className={cn("text-[var(--red-500)]")}
            error={errors.password.message}
          />
        )}
      </FormField>

      {errors.root?.message ? (
        <p className={cn("text-sm text-red-500")}>{errors.root.message}</p>
      ) : null}

      <div className={cn("flex justify-end")}>
        <Link
          href="/forgot-password"
          className={cn(
            "text-base font-medium underline underline-offset-2 text-[var(--gray-500)]",
          )}
        >
          Having trouble logging in?
        </Link>
      </div>

      <Button
        type="submit"
        variant="primary"
        className={cn("w-full h-11 rounded-lg text-base font-medium text-white")}
        disabled={isSocialLoading || isSubmitPending}
        loading={isSubmitPending}
      >
        Sign In
      </Button>

      <div className={cn("flex items-center gap-4")}>
        <div className={cn("flex-1 h-px border-t border-[var(--gray-100)]")} />
        <span
          className={cn(
            "text-base font-normal leading-none tracking-[-0.02em] text-[var(--gray-800)]",
          )}
        >
          OR
        </span>
        <div className={cn("flex-1 h-px border-t border-[var(--gray-100)]")} />
      </div>

      <div className={cn("flex flex-col gap-3")}>
        <Button
          type="button"
          variant="soft"
          className={cn(
            "w-full h-11 justify-start gap-2 text-base font-normal pl-4 text-[var(--gray-800)]",
          )}
          disabled={isSocialLoading || isSubmitPending}
          loading={isSocialLoading}
          onClick={() => void signInWithGoogle()}
        >
          <Image src="/google.png" alt="" width={23} height={23} />
          Sign in with Google
        </Button>
        {/* Apple sign-in temporarily disabled
        <Button
          type="button"
          variant="soft"
          className={cn(
            "w-full h-11 justify-start gap-2 text-base font-normal pl-4 text-[var(--gray-800)]",
          )}
          disabled={isSocialLoading}
          onClick={() => void signInWithApple()}
        >
          <Image src="/apple.png" alt="" width={23} height={23} />
          Sign in with Apple
        </Button>
        */}
      </div>

      <p
        className={cn(
          "text-center text-sm font-medium tracking-[-0.02em] text-[var(--gray-800)]",
        )}
      >
        Don&apos;t Have an Account?{" "}
        <Link
          href="/register"
          className={cn(
            "text-base font-medium underline underline-offset-2 text-[var(--gray-800)]",
          )}
        >
          Create Account!
        </Link>
      </p>
    </form>
  );
};

export default LoginForm;
